// Schedule — the week, the month, the agenda, the backlog, and everything you
// can do to a job from any of them. The classic Schedule is 1,655 lines across
// 8 files; the rebuild had only Dispatch's today/tomorrow board, so a week was
// not visible at all and nothing could be scheduled from a day.
//
//   GET   /api/jobs/schedule?start=&end=&completed=
//                                          { start, end, scheduled, backlog }
//   PATCH /api/jobs/:id/schedule           a move: only what changed
//   POST  /api/jobs                        new job, prefilled with the day
//   PUT   /api/jobs/:id                    full edit (JobForm)
//
// The grid logic lives in apps/shared/schedule.js with no React around it, so
// the multi-day rule, the month's whole-weeks boundary and the "only what
// moved" patch are tested directly.
//
// ── One request per RANGE, not per view ─────────────────────────────────────
//
// Week fetches its seven days, exactly as before. Month and Agenda share ONE
// range — the month's whole-weeks grid — so they carry the same react-query
// key and switching between them costs no request at all. Agenda is then
// scoped to the calendar month inside that range, which is why its scope line
// names Sep 1–30 while the grid also draws the last days of August.
//
// The alternative was to always fetch the month and derive the week from it
// (the month grid does cover the anchor's whole week). That was rejected: it
// makes the DEFAULT view — a week — pull five weeks of rows on every page,
// and Week is the one view this screen already had.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, describeError, serverSentence } from '../../shared/http';
import { useCan, useRole } from '../../../hooks/usePermission';
import { FwButton, FwSelect, FwField, FwInput, Pill, Sheet, Notice, Skeleton, EmptyState } from '../../../ui/fw';
import JobForm from '../../shared/JobForm';
import {
  STATUS_LABELS, JOB_STATUSES, jobBadges,
  UNASSIGNED, TYPE_FILTER_VALUES, TYPE_FILTER_LABELS,
} from '../../shared/jobFields';
import { useOfficeQuery, PageHeader, Toolbar } from '../records/OfficePage';
import { pickerNote } from '../../shared/pickerNote';
import { jobRecordHref } from '../../shared/jobLink';
import {
  weekStart, daysFrom, groupByDay, schedulePatch, weekLabel, isBlocked,
  todayISO, addDaysISO, dayLabel, dayHeading,
  monthGrid, monthLabel, monthStartISO, rangeLabel, daysBetween, agendaRows, distinctJobCount,
  COMPLETED_WINDOWS, COMPLETED_LABELS, normalizeCompleted, filterBoardJobs, boardFiltersActive,
} from '../../shared/schedule';
import './Schedule.css';

const DAYS = 7;

const VIEWS = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'agenda', label: 'Agenda' },
];

function JobChip({ job, onPick, place = false }) {
  const where = [job.client_name, job.property_name].filter(Boolean).join(' · ');
  return (
    <button type="button" className="os-chip" onClick={() => onPick(job)}>
      <span className="os-chip__title">{job.title}</span>
      {place && where ? <span className="os-chip__place">{where}</span> : null}
      <span className="os-chip__meta">
        {job.user_name || 'Unassigned'}
        {job.subtask_count ? ` · ${job.subtask_completed_count || 0}/${job.subtask_count}` : ''}
      </span>
      <span className="os-chip__tags">
        {jobBadges(job).map((b) => <Pill key={b.key} tone={b.tone}>{b.label}</Pill>)}
      </span>
    </button>
  );
}

/**
 * One month cell.
 *
 * The whole cell is the button and the job titles inside it are plain spans,
 * not buttons — a button inside a button is invalid, and 48px-wide cells on a
 * phone could not carry a per-job tap target anyway. Acting on a job goes
 * through the day panel underneath, which is the same path at every width.
 *
 * `data-in-month` is what the stylesheet dims. The titles are hidden by CSS
 * below the phone breakpoint and the count carries the cell on its own there.
 */
function MonthCell({ day, jobs, selected, today, onPick }) {
  const shown = jobs.slice(0, 3);
  const label = dayLabel(day.iso);
  return (
    <button
      type="button"
      className="os-mcell"
      data-day={day.iso}
      data-in-month={day.inMonth ? 'true' : 'false'}
      data-today={day.iso === today ? 'true' : undefined}
      aria-pressed={day.iso === selected}
      aria-label={`${label.wd} ${label.n}, ${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`}
      onClick={() => onPick(day.iso)}
    >
      <span className="os-mcell__head">
        <span className="os-mcell__n fw-num">{day.n}</span>
        {jobs.length ? <span className="os-mcell__count fw-num" aria-hidden="true">{jobs.length}</span> : null}
      </span>
      <span className="os-mcell__chips" aria-hidden="true">
        {shown.map((j) => (
          <span key={j.id} className="os-mcell__chip" data-blocked={isBlocked(j) ? 'true' : undefined}>
            {j.title}
          </span>
        ))}
        {jobs.length > shown.length ? (
          <span className="os-mcell__more">+{jobs.length - shown.length} more</span>
        ) : null}
      </span>
    </button>
  );
}

/** Move, reassign, block or unblock — the things you do from a calendar. */
// `workersError` for the same reason JobForm takes `listErrors`: an empty
// crew <select> reads as "nobody works here". It is worse on this sheet than
// on the form, because the job being moved may already HAVE a lead — the
// select then holds a value with no matching <option> and paints the first
// one, so a dispatcher is shown "Unassigned" for an assigned job. Nothing is
// lost (schedulePatch compares against the job, sees no change and sends no
// user_id), but the screen is stating something untrue about a crew's day.
//
// This one control IS disabled while the list is missing, which the JobForm
// pickers deliberately are not. The difference is what the remaining option
// does: on the form, saving without a client is a real fallback a person may
// want; here the only reachable choice is "Unassigned", which would take the
// lead off a job — an action nobody arrived intending, chosen from a control
// that is already displaying the wrong answer.
function MoveSheet({ job, workers, workersError = null, onClose, onSaved }) {
  const navigate = useNavigate();
  // The job itself — photos, work list, notes, edit, delete — one tap from the
  // calendar. Classic's sheet had "Edit full job"; this one only moved it.
  const recordHref = jobRecordHref(job.id);
  const [form, setForm] = useState(() => ({
    scheduled_date: job.scheduled_date || '',
    scheduled_end_date: job.scheduled_end_date || '',
    user_id: job.user_id == null ? '' : String(job.user_id),
    status: job.status || 'todo',
    blocked_reason: job.blocked_reason || '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const body = schedulePatch(job, form);
    if (!Object.keys(body).length) { onClose(); return; }   // nothing moved
    setBusy(true);
    setError('');
    try {
      await patch(`/jobs/${job.id}/schedule`, body);
      await onSaved();
      onClose();
    } catch (e) {
      setError(describeError(e, 'Could not move the job.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      title={job.title}
      onClose={onClose}
      footer={(
        <div className="os-sheet__footer">
          {recordHref ? (
            <FwButton variant="quiet" icon="arrowRight" onClick={() => navigate(recordHref)} disabled={busy} data-action="open-job">Open job</FwButton>
          ) : null}
          <FwButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</FwButton>
          <FwButton variant="primary" onClick={save} loading={busy}>Save</FwButton>
        </div>
      )}
    >
      <div className="fw-form">
        {error ? <Notice tone="stop">{error}</Notice> : null}
        <FwField label="Scheduled" htmlFor="os-date">
          <FwInput id="os-date" type="date" value={form.scheduled_date} onChange={set('scheduled_date')} />
        </FwField>
        <FwField label="Until" htmlFor="os-end" hint="Leave blank for a single day.">
          <FwInput id="os-end" type="date" value={form.scheduled_end_date} onChange={set('scheduled_end_date')} />
        </FwField>
        <FwField
          label="Lead worker"
          htmlFor="os-lead"
          error={workersError
            ? `${pickerNote(workersError, 'worker')}${job.user_name ? ` This job is still led by ${job.user_name}.` : ''} Reload before changing it.`
            : null}
        >
          <FwSelect id="os-lead" value={form.user_id} onChange={set('user_id')} disabled={!!workersError}>
            <option value="">Unassigned</option>
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Status" htmlFor="os-status">
          <FwSelect id="os-status" value={form.status} onChange={set('status')}>
            {JOB_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </FwSelect>
        </FwField>
        <FwField
          label="Blocked because"
          htmlFor="os-block"
          hint="Empty means not blocked. Clearing this unblocks the job."
        >
          <FwInput id="os-block" value={form.blocked_reason} onChange={set('blocked_reason')} maxLength={200} />
        </FwField>
      </div>
    </Sheet>
  );
}

export default function Schedule() {
  const qc = useQueryClient();
  const role = useRole();
  // useCan takes (resource, action). These read `useCan('jobs.create')` until
  // 2026-09-03, which asks for a RESOURCE named "jobs.create" — no role has
  // one, so `perms['jobs.create']` was undefined and the hook returned false
  // for everybody except an admin, who short-circuits before the lookup.
  //
  // The effect was not subtle: a MANAGER saw no New job, no Edit, no Delete,
  // no status change and no Mark blocked on this screen, and could not
  // schedule or move a job on the Schedule — while the server happily allowed
  // all of it (permissions.js gives manager jobs: create/read/update/delete).
  // Admin-only testing hides this completely, because the admin branch never
  // reaches the resource lookup.
  const canCreate = useCan('jobs', 'create');
  const canUpdate = useCan('jobs', 'update');

  const { settings } = useOfficeSettings();
  const startOfWeek = Number(settings.start_of_week ?? 1);

  const [view, setView] = useState('week');            // week is the default, unchanged
  const [anchor, setAnchor] = useState(() => todayISO());
  const [picked, setPicked] = useState(null);          // the month view's chosen day

  // ── The filters, in the URL ────────────────────────────────────────────────
  //
  // Same mechanism and same shape as screens/Invoices.js, Estimates.js and
  // Payroll.js: the SELECTS live in the query string and are written with
  // `{ replace: true }`, so a dispatcher who narrows the board to one crew
  // member and then reloads — a deploy, a dropped signal in a truck, a phone
  // that slept — is still looking at the same board, and "look at this" is a
  // link. A default is absent from the URL rather than written to it, so a
  // clean screen has a clean address.
  //
  // A param is normalised against what the control can OFFER, never trusted:
  // a <select> holding a value with no matching <option> paints the first one,
  // so `?type=nonsense` would show "All types" over a board filtered to
  // nothing — a control displaying the opposite of what it is doing.
  const [params, setParams] = useSearchParams();
  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    next.delete('type');
    next.delete('crew');
    setParams(next, { replace: true });
  };

  const rawType = params.get('type') || '';
  const type = TYPE_FILTER_VALUES.includes(rawType) ? rawType : '';
  const completed = normalizeCompleted(params.get('completed'));

  const start = weekStart(anchor, startOfWeek);
  const days = useMemo(() => daysFrom(start, DAYS), [start]);
  const end = days[days.length - 1];

  // Built for every view: it is where the month's boundaries come from, and
  // recomputing five strings is cheaper than a conditional hook.
  const grid = useMemo(() => monthGrid(anchor, startOfWeek), [anchor, startOfWeek]);
  const gridIsos = useMemo(() => grid.days.map((d) => d.iso), [grid]);
  const monthDays = useMemo(() => daysBetween(grid.monthStart, grid.monthEnd), [grid]);

  // Month and Agenda share this range, so switching between them is a cache
  // hit rather than a request. See the note at the top of the file.
  const range = view === 'week' ? { start, end } : { start: grid.start, end: grid.end };

  // `completed` is in the KEY as well as the URL: it changes the answer, so a
  // window that only changed the request would be served the previous
  // window's rows out of the cache and read as a control that does nothing.
  const scheduleQ = useQuery({
    queryKey: ['schedule', range.start, range.end, completed],
    queryFn: () => get(`/jobs/schedule?start=${range.start}&end=${range.end}&completed=${completed}`),
  });
  const clientsQ = useOfficeQuery(['clients'], '/clients');
  const propsQ = useOfficeQuery(['properties'], '/clients/properties/all');
  const usersQ = useOfficeQuery(['admin-users'], '/admin/users', { enabled: role === 'admin' || role === 'manager' });

  const [moving, setMoving] = useState(null);
  const [sheet, setSheet] = useState(null);          // { job, day } | null

  // Derived inside the memo: `Array.isArray(...) ? ... : []` builds a NEW
  // empty array on every render, so using it as a dependency would regroup
  // the whole week each time anything else changed.
  const allBacklog = useMemo(() => {
    const rows = scheduleQ.data && scheduleQ.data.backlog;
    return Array.isArray(rows) ? rows : [];
  }, [scheduleQ.data]);
  const allScheduled = useMemo(() => {
    const rows = scheduleQ.data && scheduleQ.data.scheduled;
    return Array.isArray(rows) ? rows : [];
  }, [scheduleQ.data]);
  const workers = useMemo(
    () => (usersQ.data || []).filter((u) => u.is_active !== 0).map((u) => ({ id: u.id, name: u.name })),
    [usersQ.data],
  );

  // Only a role that can see other people's work is offered a worker filter —
  // /admin/users is what fills it and `usersQ` is enabled for nobody else, so
  // for a crew member the control would be an empty picker that reads as "there
  // is no crew". The PARAM is dropped with it: a filter nobody can see is one
  // nobody can undo, and a link carrying ?crew= would otherwise hide most of a
  // board with no visible cause.
  const canFilterByWorker = role === 'admin' || role === 'manager';
  const rawWorker = canFilterByWorker ? (params.get('crew') || '') : '';
  // An id the roster does not contain is dropped ONCE THE ROSTER HAS ANSWERED,
  // for the reason the move sheet carries a paragraph about: a <select> holding
  // a value with no matching <option> paints the first one, so a stale link
  // would show "All crew" over a board narrowed to somebody who has left.
  const worker = (!rawWorker || rawWorker === UNASSIGNED
    || !usersQ.isSuccess
    || workers.some((w) => String(w.id) === rawWorker)) ? rawWorker : '';

  const filters = useMemo(() => ({ type, worker }), [type, worker]);
  const filtered = boardFiltersActive(filters);
  const backlog = useMemo(() => filterBoardJobs(allBacklog, filters), [allBacklog, filters]);
  const scheduled = useMemo(() => filterBoardJobs(allScheduled, filters), [allScheduled, filters]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['schedule'] });
  const today = todayISO();

  // ── The screen opens where the work is, not where the week begins ─────────
  //
  // Measured in Chromium on Thursday 2026-09-10, one job on every day of the
  // week, signed in as admin, reading `.os-day--today`:
  //
  //     320x780    MON 7 at y=609 was the ONLY day on screen; TODAY at y=1210
  //     390x780    MON 7 (557) and TUE 8 (757) on screen; TODAY at y=1158
  //     1440x900   all seven days at y=360 — the whole week already in view
  //
  // A dispatcher opening this to plan the day they are standing in got three
  // days that are already gone and had to scroll past them, every time. On a
  // desktop there was nothing wrong: the week is seven columns and all of it
  // is on the screen at once.
  //
  // THE SEVEN DAYS ARE NOT CHANGED, deliberately. `weekStart` follows the
  // company's start_of_week setting — seeded '1', Monday (server/database.js)
  // — because payroll weeks are computed from that same setting server-side
  // (server/utils/dateRange.js) and the month grid reads it too. A rolling
  // "seven days from today" would put the calendar's week boundary somewhere
  // other than the timesheet covering those days, and would make Previous and
  // Next page something the week label above them does not describe.
  //
  // So what moved is where the screen LANDS. Only when today is not already
  // fully in view, which is why the desktop above does not move at all:
  // scrolling there would push the view switcher, the week label and the
  // paging controls off the top and buy nothing.
  //
  // Once per visit. Landing is something a screen does when it opens; redoing
  // it on every render would yank the page out from under someone reading it.
  const weekGridRef = useRef(null);
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current) return;
    if (view !== 'week' || !scheduleQ.isSuccess) return;
    if (!days.includes(today)) return;                   // paged away from this week
    const el = weekGridRef.current && weekGridRef.current.querySelector('.os-day--today');
    if (!el || typeof el.scrollIntoView !== 'function') return;
    landed.current = true;
    const box = el.getBoundingClientRect();
    const viewport = window.innerHeight || 0;
    if (box.top >= 0 && box.bottom <= viewport) return;  // already on screen — leave it alone
    el.scrollIntoView({ block: 'start' });
  }, [view, days, today, scheduleQ.isSuccess]);

  const byDay = useMemo(() => groupByDay(scheduled, days), [scheduled, days]);
  const byGridDay = useMemo(() => groupByDay(scheduled, gridIsos), [scheduled, gridIsos]);
  const agenda = useMemo(() => agendaRows(scheduled, monthDays, today), [scheduled, monthDays, today]);

  const weekCount = distinctJobCount(byDay);
  const monthCount = distinctJobCount(byGridDay);
  const agendaCount = distinctJobCount(agenda);
  // The sentence must not claim the MONTH is empty when a filter is what
  // emptied it. "Nothing is scheduled in September" over a board narrowed to
  // one crew member is a false statement about the company's work, and it is
  // the kind that gets believed because it reads like data.
  const nothingHere = filtered
    ? `No job matches these filters in ${monthLabel(anchor)}.`
    : `Nothing is scheduled in ${monthLabel(anchor)}.`;

  // ── Done: the finished work the calendar cannot show ──────────────────────
  //
  // The endpoint folds completed jobs into `scheduled` (routes/jobs.js:420),
  // and the ones that are ON the calendar are drawn on their own day. The rest
  // are not drawn anywhere: a job finished with no scheduled_date at all, or
  // one scheduled outside the range on screen. Before this rail they were
  // fetched on every single request and thrown away by groupByDay — which is
  // also what made the `completed` window unobservable, so restoring the
  // control without somewhere for its answer to land would have been a filter
  // that changes nothing a person can see.
  const onCalendar = useMemo(() => {
    const ids = new Set();
    (view === 'week' ? byDay : byGridDay).forEach((jobs) => jobs.forEach((j) => ids.add(j.id)));
    return ids;
  }, [view, byDay, byGridDay]);
  const completedRows = useMemo(() => scheduled.filter((j) => j.status === 'completed'), [scheduled]);
  const finished = useMemo(
    () => completedRows.filter((j) => !onCalendar.has(j.id)),
    [completedRows, onCalendar],
  );

  // Clamped into the visible grid ON READ, never resolved once into state.
  // Paging the month moves `grid` and state cannot follow: a day picked in one
  // month and then pointed outside the next grid left no cell selected while
  // the panel underneath answered about a day that was not on screen. That is
  // the bug the classic MonthView carries a comment about, and it is the same
  // shape here.
  const inGrid = (iso) => !!iso && gridIsos.includes(iso);
  const selected = inGrid(picked) ? picked : (inGrid(today) ? today : null);
  const selectedJobs = selected ? (byGridDay.get(selected) || []) : [];

  const isMonthish = view !== 'week';
  const meta = isMonthish ? monthLabel(anchor) : weekLabel(start, DAYS);
  // Paging a month lands on the 1st of the target month, not on the last day
  // of it. The anchor is shared with the week view — switching views keeps
  // your place rather than jumping back to today — so an anchor left on the
  // 30th would open Week on the LAST week of the month you were looking at.
  // Landing on the 1st also makes Previous-then-Next return where it started.
  const stepBack = () => setAnchor(isMonthish
    ? monthStartISO(addDaysISO(grid.monthStart, -1))
    : addDaysISO(start, -DAYS));
  const stepFwd = () => setAnchor(isMonthish
    ? addDaysISO(grid.monthEnd, 1)                       // already the 1st
    : addDaysISO(start, DAYS));

  const pickJob = canUpdate ? setMoving : () => {};

  const clearButton = (
    <FwButton variant="ghost" onClick={clearFilters} className="os-filters__clear">Clear filters</FwButton>
  );

  const weekBody = (
    <>
      {weekCount === 0 ? (
        <Notice tone="info" className="os-week__empty" data-week-empty>
          {filtered ? (
            <>
              {`No job in this week matches these filters.`}
              {' '}
              {clearButton}
            </>
          ) : 'Nothing is scheduled this week.'}
        </Notice>
      ) : null}
      <div className="os-week" ref={weekGridRef}>
      {days.map((d) => {
        const label = dayLabel(d);
        const jobs = byDay.get(d) || [];
        const isToday = d === today;
        return (
          <section key={d} className={`os-day${isToday ? ' os-day--today' : ''}`}>
            <header className="os-day__head">
              <span className="os-day__wd">{label.wd}</span>
              <span className="os-day__n fw-num">{label.n}</span>
              {/* A coloured border is not a name. After the screen scrolls to
                  this day, it is the only thing saying WHY you are here — and
                  a person landing mid-week needs to read it, not infer it.
                  The word comes from utils/agenda.js RELATIVE_LABEL, through
                  dayHeading, so the week and the agenda cannot drift apart.
                  Only today: "Yesterday" and "Tomorrow" on three of seven
                  columns is noise on a screen whose complaint is that nobody
                  can tell what is what at a glance. */}
              {isToday ? <Pill tone="now">{dayHeading(d, today).relativeLabel}</Pill> : null}
            </header>
            <div className="os-day__body">
              {/*
                "Nothing on", not "—".

                An em dash is the screen declining to answer, and this one was
                on six of the seven columns of an ordinary week — measured at
                320px on 2026-09-11, a week with work on one day only rendered
                the character six times and no words at all. docs/APP_REBUILD.md
                is explicit that a fact slot with nothing in it should carry a
                real fact or nothing, and "a day with no work must look
                different from a day that failed to load" cannot be true of a
                glyph that says neither.

                The wording is the crew board's — apps/dispatch/screens/Board.js
                says "Nothing on" for an empty cell in its week matrix — rather
                than a sixth spelling of empty. The three sentences on this
                screen stay distinct on purpose: this is one DAY, the notice
                above is the whole WEEK, and the month panel's is the day you
                picked.

                A request that did not answer never reaches here: `scheduleQ.error`
                replaces the entire body with the server's own sentence, because
                the grid, the agenda and the backlog all come out of that one
                request. Asserted in OfficeSchedule.test.js so the two can never
                start looking alike.
              */}
              {jobs.length
                ? jobs.map((j) => <JobChip key={`${d}-${j.id}`} job={j} onPick={pickJob} />)
                : <p className="os-day__empty">Nothing on</p>}
            </div>
            {canCreate ? (
              <FwButton
                variant="quiet"
                icon="plus"
                className="os-day__add"
                aria-label={`Add a job on ${d}`}
                onClick={() => setSheet({ job: null, day: d })}
              >
                Add
              </FwButton>
            ) : null}
          </section>
        );
      })}
      </div>
    </>
  );

  const monthBody = (
    <div className="os-month" data-month={grid.monthStart.slice(0, 7)}>
      <div className="os-month__dow" aria-hidden="true">
        {grid.weeks[0].map((d) => (
          <span key={d.iso} className="os-month__dowcell">{dayLabel(d.iso).wd}</span>
        ))}
      </div>
      <div className="os-month__grid" role="group" aria-label={`${monthLabel(anchor)} calendar`}>
        {grid.days.map((d) => (
          <MonthCell
            key={d.iso}
            day={d}
            jobs={byGridDay.get(d.iso) || []}
            selected={selected}
            today={today}
            onPick={setPicked}
          />
        ))}
      </div>

      {monthCount === 0 ? (
        <Notice tone="info" className="os-month__empty">
          {nothingHere}
          {filtered ? <> {clearButton}</> : null}
        </Notice>
      ) : null}

      <section className="os-mday" data-day-panel={selected || ''}>
        {selected ? (
          <>
            <h2 className="os-mday__title">
              {dayHeading(selected, today).relativeLabel
                ? `${dayHeading(selected, today).relativeLabel} — ${dayHeading(selected, today).dateLabel}`
                : dayHeading(selected, today).dateLabel}
            </h2>
            {selectedJobs.length ? (
              <div className="os-backlog__list">
                {selectedJobs.map((j) => <JobChip key={j.id} job={j} onPick={pickJob} place />)}
              </div>
            ) : (
              <p className="os-mday__empty">Nothing scheduled on this day.</p>
            )}
            {canCreate ? (
              <FwButton
                variant="quiet"
                icon="plus"
                className="os-mday__add"
                aria-label={`Add a job on ${selected}`}
                onClick={() => setSheet({ job: null, day: selected })}
              >
                Add a job on this day
              </FwButton>
            ) : null}
          </>
        ) : (
          <p className="os-mday__empty">Pick a day to see its jobs.</p>
        )}
      </section>
    </div>
  );

  const agendaBody = (
    <div className="os-agenda">
      {/* What the list is scoped to, said rather than implied. The grid above a
          month also draws the tail of the previous month; this list does not,
          so the dates are named. */}
      <p className="os-agenda__scope" data-agenda-scope>
        {agendaCount === 1 ? '1 job' : `${agendaCount} jobs`} in {monthLabel(anchor)}
        {' · '}
        {rangeLabel(grid.monthStart, grid.monthEnd)}
      </p>

      {agendaCount === 0 ? (
        <EmptyState
          icon="calendar"
          title={nothingHere}
          action={filtered ? clearButton : null}
        >
          {filtered
            ? 'Everything in these dates is filtered out. Clear the filters, or try another month.'
            : 'Nothing falls in these dates. Try another month, or place something from the backlog below.'}
        </EmptyState>
      ) : agenda.map((row) => (
        <section
          key={row.iso}
          className="os-agenda__day"
          data-agenda-day={row.iso}
          data-relative={row.relative || undefined}
        >
          <header className="os-agenda__head">
            <span className="os-agenda__when">
              {row.relativeLabel ? <Pill tone="now">{row.relativeLabel}</Pill> : null}
              <span className="os-agenda__date">{row.dateLabel}</span>
            </span>
            <span className="os-agenda__count fw-num">
              {row.jobs.length} {row.jobs.length === 1 ? 'job' : 'jobs'}
            </span>
          </header>
          {row.jobs.length ? (
            <div className="os-agenda__rows">
              {row.jobs.map((j) => <JobChip key={j.id} job={j} onPick={pickJob} place />)}
            </div>
          ) : (
            <p className="os-agenda__none">Nothing scheduled.</p>
          )}
        </section>
      ))}
    </div>
  );

  return (
    <>
      <PageHeader title="Schedule" meta={meta}>
        {canCreate ? (
          <FwButton variant="primary" icon="plus" onClick={() => setSheet({ job: null, day: today })}>New job</FwButton>
        ) : null}
      </PageHeader>

      <Toolbar>
        <div className="or-seg" role="group" aria-label="Schedule view">
          {VIEWS.map((v) => (
            <FwButton
              key={v.key}
              variant={view === v.key ? 'primary' : 'ghost'}
              aria-pressed={view === v.key}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </FwButton>
          ))}
        </div>
        <FwButton variant="ghost" icon="chevronLeft" onClick={stepBack}>Previous</FwButton>
        <FwButton variant="ghost" onClick={() => setAnchor(today)}>
          {isMonthish ? 'This month' : 'This week'}
        </FwButton>
        <FwButton variant="ghost" icon="chevronRight" onClick={stepFwd}>Next</FwButton>
      </Toolbar>

      {/* docs/PARITY_2026-09-08.md §12. Its own row rather than more controls
          in the toolbar: the toolbar already carries the view switcher and
          three paging buttons, and at 320px a fourth group in the same wrap
          container puts the filters below the fold of a screen nobody scrolls
          before deciding the board is wrong. The row is a grid of
          minmax(min(…, 100%), 1fr) tracks, so it is one column on a phone and
          as many as fit on a desk — and no track can be wider than what holds
          it (APP_REBUILD rule 2). */}
      <div className="os-filters">
        <FwField label="Type" htmlFor="os-type">
          <FwSelect
            id="os-type"
            aria-label="Filter by type"
            value={type}
            onChange={(e) => setParam('type', e.target.value)}
          >
            {TYPE_FILTER_VALUES.map((v) => (
              <option key={v || 'all'} value={v}>{TYPE_FILTER_LABELS[v]}</option>
            ))}
          </FwSelect>
        </FwField>

        {canFilterByWorker ? (
          /* The same `usersQ.error` the move sheet and the job form already
             read. An empty crew picker reads as "nobody works here" rather
             than as a request that failed, and nobody questions data. */
          <FwField label="Crew" htmlFor="os-crew" error={pickerNote(usersQ.error, 'worker')}>
            <FwSelect
              id="os-crew"
              aria-label="Filter by worker"
              value={worker}
              onChange={(e) => setParam('crew', e.target.value)}
            >
              <option value="">All crew</option>
              <option value={UNASSIGNED}>Unassigned</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </FwSelect>
          </FwField>
        ) : null}

        {filtered ? clearButton : null}
      </div>

      {/* A refusal replaces the whole body — the grid, the agenda AND the
          backlog all come out of this one request, so an empty calendar drawn
          over a 403 would read as "nothing is scheduled". The server's own
          sentence, via serverSentence, because describeError flattens every
          403 to "You do not have permission to do that." and loses which door
          was shut. The switcher and the paging stay so a person can move to a
          range they are allowed to see. */}
      {scheduleQ.error ? (
        <Notice tone="stop" className="or-error" data-schedule-error>
          {serverSentence(scheduleQ.error, 'Could not load the schedule.')}
        </Notice>
      ) : scheduleQ.isLoading ? (
        <Skeleton lines={10} />
      ) : (
        <>
          {view === 'week' ? weekBody : view === 'month' ? monthBody : agendaBody}

          <section className="os-backlog">
            <h2 className="os-backlog__title">Not scheduled ({backlog.length})</h2>
            {backlog.length ? (
              <div className="os-backlog__list">
                {/* `place` here, unlike the week columns above. The backlog
                    is a full-width auto-fill grid (`minmax(min(220px, 100%),
                    1fr)`, Schedule.css) — there is room — and it is the one
                    list on this screen with NO day to identify a job by, so
                    "which Hedge trim is this" had no answer at all. The owner,
                    2026-09-10: "who's assigned/unassigned, client, property".
                    The seven day columns stay without it; they are narrow, and
                    a job in them is already placed by its column. */}
                {backlog.map((j) => <JobChip key={j.id} job={j} onPick={pickJob} place />)}
              </div>
            ) : (
              <EmptyState
                title={filtered
                  ? 'No unscheduled job matches these filters.'
                  : 'Everything workable is on the calendar.'}
                action={filtered ? clearButton : null}
              />
            )}
          </section>

          {/* Done — and the window that decides how far back it reaches.
              The control sits HERE, on the thing it changes, exactly as the
              classic board carried it in its Done column header. */}
          <section className="os-done" data-done-window={completed}>
            <div className="os-done__head">
              <h2 className="os-backlog__title">Done ({finished.length})</h2>
              <FwField label="Finished work" htmlFor="os-completed" className="os-done__window">
                <FwSelect
                  id="os-completed"
                  aria-label="Completed time window"
                  value={completed}
                  onChange={(e) => setParam('completed', e.target.value === 'recent' ? '' : e.target.value)}
                >
                  {COMPLETED_WINDOWS.map((w) => (
                    <option key={w} value={w}>{COMPLETED_LABELS[w]}</option>
                  ))}
                </FwSelect>
              </FwField>
            </div>
            <p className="os-done__note">
              Finished work that is not on the calendar above — a job completed with no
              date on it, or one whose day is outside the dates on screen.
            </p>
            {finished.length ? (
              <div className="os-backlog__list">
                {finished.map((j) => <JobChip key={j.id} job={j} onPick={pickJob} place />)}
              </div>
            ) : (
              <p className="os-done__none">
                {completedRows.length
                  ? 'Every job this window covers is already on the calendar above.'
                  : 'No finished work in this window.'}
              </p>
            )}
          </section>
        </>
      )}

      {moving ? (
        <MoveSheet job={moving} workers={workers} workersError={usersQ.error} onClose={() => setMoving(null)} onSaved={refresh} />
      ) : null}

      <JobForm
        open={!!sheet}
        job={sheet ? sheet.job : null}
        role={role}
        clients={clientsQ.data || []}
        properties={propsQ.data || []}
        workers={workers}
        // `|| []` above cannot tell an empty list from a refused request, and an
        // empty <select> reads as data. See apps/shared/pickerNote.js.
        listErrors={{ clients: clientsQ.error, properties: propsQ.error, workers: usersQ.error }}
        defaultDate={sheet ? sheet.day : ''}
        onClose={() => setSheet(null)}
        onSaved={refresh}
      />
    </>
  );
}

/**
 * The company's start_of_week, which the calendar must agree with — payroll
 * weeks are computed from the same setting server-side.
 *
 * GET /api/settings (authenticated) answers a key → value OBJECT of every
 * setting. GET /api/settings/public does NOT carry start_of_week and answers
 * an ARRAY of { key, value } rows, so it is the wrong door for this.
 * Values are stored as strings: '0' is Sunday and must survive, so the
 * fallback uses ?? rather than ||.
 */
function useOfficeSettings() {
  const q = useOfficeQuery(['settings'], '/settings');
  return { settings: q.data || {} };
}
